import { progressPct } from "@/lib/progress";
import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import KanbanBoard from "@/components/KanbanBoard";
import TimelineView from "@/components/TimelineView";
import MeetingPrep from "@/components/MeetingPrep";
import RaciView from "@/components/RaciView";
import ReportsView from "@/components/ReportsView";
import RiskRegister from "@/components/RiskRegister";
import BudgetView from "@/components/BudgetView";
import BaselineView from "@/components/BaselineView";
import ProjectMap from "@/components/ProjectMap";
import RoadmapView from "@/components/RoadmapView";
import TaskDialog from "@/components/TaskDialog";
import AIPlanDialog from "@/components/AIPlanDialog";
import TrueUpDialog from "@/components/TrueUpDialog";
import ProjectSettingsDialog from "@/components/ProjectSettingsDialog";
import { PhaseNameDialog, PhaseImportDialog } from "@/components/PhaseTemplateDialogs";
import ActionItems from "@/components/ActionItems";
import RaidView from "@/components/RaidView";
import ExecSummaryDialog from "@/components/ExecSummaryDialog";
import ResourceLeveling from "@/components/ResourceLeveling";
import PhaseProgress from "@/components/PhaseProgress";
import WbsTable from "@/components/WbsTable";
import AuditView from "@/components/AuditView";
import ArtifactsView from "@/components/ArtifactsView";
import ApprovalsView from "@/components/ApprovalsView";
import CardsView from "@/components/CardsView";
import StoryBacklog from "@/components/StoryBacklog";
import NotebookView from "@/components/NotebookView";
import OrchestraPit from "@/components/OrchestraPit";
import ProcessView from "@/components/ProcessView";
import PlanToProcessButton from "@/components/PlanToProcessButton";
import { exportWbsCsv, printWbs } from "@/lib/wbsExport";
import StatTasksDialog from "@/components/StatTasksDialog";
import SaveTemplateDialog from "@/components/SaveTemplateDialog";
import SavePlanTemplateDialog from "@/components/SavePlanTemplateDialog";
import Markdown from "@/components/Markdown";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const TAB_GROUPS = [
  { label: "Plan", items: [["plan", "Plan (WBS)"], ["ioflow", "IO Flow"], ["mywork", "My Work"], ["timeline", "Gantt"], ["roadmap", "Roadmap · Critical path"], ["map", "Visual"], ["board", "Board"], ["baseline", "Baseline"]] },
  { label: "Track", items: [["reports", "Reports"], ["raid", "RAID"], ["radar", "On the Radar"], ["audit", "Audit"], ["approvals", "Approvals"], ["statusreport", "Status Report"]] },
  { label: "People", items: [["resources", "Resources"], ["stakeholders", "Stakeholders"], ["teams", "Teams (RACI)"], ["cards", "Cards"]] },
  { label: "Communicate", items: [["meeting", "Meetings"], ["notebook", "Notebook"], ["pit", "Orchestra Pit"]] },
  { label: "Library", items: [["artifacts", "Artifacts"], ["approvals", "Approvals"], ["process", "Process"], ["budget", "Budget"]] },
];
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { PMBOK_GROUPS, derivePhases, buildPhaseColors, statusMeta, priorityMeta } from "@/lib/constants";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronDown, ChevronRight, Plus, Sparkles, FileText, Loader2, Briefcase, ListTodo, Trash2, BarChart3, ShieldAlert, MessageCircle, Settings, CalendarClock, X, ClipboardList, Presentation, BookMarked, AlertTriangle, CheckCircle2, Download, Printer, FileUp, Link2, GripVertical, Archive, Pencil, UserPlus, Hourglass, GraduationCap, Wand2,
} from "lucide-react";
import ImportWizard from "@/components/ImportWizard";
import LinkTasksDialog from "@/components/LinkTasksDialog";
import { PhaseFlowView } from "@/components/PhaseFlowView";
import IoMapBuilderDialog from "@/components/IoMapBuilderDialog";
import StakeholderView from "@/components/StakeholderView";
import ResourcesView from "@/components/ResourcesView";
import ProjectMembers from "@/components/ProjectMembers";
import MyWorkView from "@/components/MyWorkView";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = ["plan", "mywork", "timeline", "map", "roadmap", "board", "reports", "budget", "resources", "stakeholders", "baseline", "risks", "actions", "raid", "radar", "teams", "meeting", "notebook", "pit", "process", "audit", "artifacts", "approvals", "cards", "statusreport"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "plan";
  const [tab, setTab] = useState(initialTab);
  const [boardView, setBoardView] = useState("board");
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskSeed, setTaskSeed] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkTaskId, setLinkTaskId] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [planTemplateOpen, setPlanTemplateOpen] = useState(false);
  const [wizardSuggestHidden, setWizardSuggestHidden] = useState(false);
  const [trueup, setTrueup] = useState(null);
  const [trueupOpen, setTrueupOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [execOpen, setExecOpen] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [planCollapsed, setPlanCollapsed] = useState({});
  const [planView, setPlanView] = useState("wbs");
  const [statOpen, setStatOpen] = useState(null);
  const [chatUnread, setChatUnread] = useState(0);
  const [radarInfo, setRadarInfo] = useState(null);
  useEffect(() => {
    api.get(`/projects/${id}/radar`).then(({ data }) => setRadarInfo(data)).catch(() => {});
  }, [id, tab]);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const [savingDesc, setSavingDesc] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const togglePlanPhase = (g) => setPlanCollapsed((prev) => ({ ...prev, [g]: !prev[g] }));

  const putProject = async (fields) => {
    const { data } = await api.put(`/projects/${id}`, {
      name: project.name, description: project.description || "", industry: project.industry || "",
      trueup_frequency: project.trueup_frequency || null, trueup_custom_days: project.trueup_custom_days ?? null,
      start_date: project.start_date || null, end_date: project.end_date || null,
      work_on_weekends: !!project.work_on_weekends, timezone: project.timezone || null,
      duration_unit: project.duration_unit || null, holiday_calendar: project.holiday_calendar || null,
      custom_holidays: project.custom_holidays || null, phase_colors: project.phase_colors || null,
      flow_colors: project.flow_colors || null, summary_color: project.summary_color || null,
      report_lane_order: project.report_lane_order || null, program: project.program || null, ...fields,
    });
    setProject(data);
    return data;
  };

  const saveDescription = async () => {
    setSavingDesc(true);
    try {
      await putProject({ description: descDraft.trim() });
      setEditingDesc(false);
      toast.success("Description updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save the description"); }
    finally { setSavingDesc(false); }
  };

  const saveName = async () => {
    if (!nameDraft.trim()) return;
    setSavingName(true);
    try {
      await putProject({ name: nameDraft.trim() });
      setEditingName(false);
      toast.success("Project renamed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not rename the project"); }
    finally { setSavingName(false); }
  };

  useEffect(() => {
    if (project?.my_role === "member" && !searchParams.get("tab")) setTab("mywork");
  }, [project?.my_role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    const fetchUnread = () => {
      api.get(`/projects/${id}/chat/unread`).then(({ data }) => setChatUnread(data.count || 0)).catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [id]);

  // Sync tab + focus a task when URL params change (e.g. from Cards "open in plan")
  const focusTaskId = searchParams.get("task");
  const handledFocus = useRef(null);
  useEffect(() => { if (id) localStorage.setItem("last-project-id", id); }, [id]);
  const isIoMap = (project?.methodology || "").startsWith("IO Map");
  const [ioMapOpen, setIoMapOpen] = useState(false);
  useEffect(() => { if (isIoMap) setPlanView("flow"); }, [isIoMap]);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== tab && ["plan", "mywork", "timeline", "map", "roadmap", "board", "reports", "budget", "resources", "stakeholders", "baseline", "risks", "actions", "raid", "teams", "meeting", "notebook", "pit", "process", "audit", "artifacts", "cards", "statusreport", "approvals"].includes(t)) setTab(t);
    if (searchParams.get("view") === "flow") setPlanView("flow");
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!focusTaskId || loading || !tasks.length || handledFocus.current === focusTaskId) return;
    const t = tasks.find((x) => x.id === focusTaskId);
    if (!t) return;
    handledFocus.current = focusTaskId;
    setTab("plan");
    setPlanView("wbs");
    if (t.phase) setPlanCollapsed((prev) => ({ ...prev, [t.phase]: false }));
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="wbs-row-${focusTaskId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("wbs-row-flash");
        setTimeout(() => el.classList.remove("wbs-row-flash"), 3200);
      }
    }, 350);
  }, [focusTaskId, loading, tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTrueup = async () => {
    try {
      const { data } = await api.get(`/projects/${id}/trueup`);
      setTrueup(data);
    } catch (e) { /* non-blocking */ }
  };

  const load = async () => {
    try {
      const [p, t] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/tasks`),
      ]);
      setProject(p.data);
      setTasks(t.data);
      loadTrueup();
    } catch (e) {
      toast.error("Could not load project");
      nav("/dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onDataChanged = (e) => { if (e.detail?.projectId === id) load(); };
    window.addEventListener("orbit-data-changed", onDataChanged);
    return () => window.removeEventListener("orbit-data-changed", onDataChanged);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/projects").then(({ data }) => setAllProjects(data)).catch(() => {}); }, []);

  const stats = useMemo(() => {
    const total = tasks.filter((t) => !t.is_summary).length;
    const isDone = (t) => t.health === "blue" || t.status === "done" || (t.percent_complete || 0) >= 100;
    const effPct = (t) => (t.percent_complete != null ? Math.max(0, Math.min(100, t.percent_complete)) : (isDone(t) ? 100 : 0));
    const done = tasks.filter((t) => !t.is_summary && isDone(t)).length;
    const critical = tasks.filter((t) => t.priority === "critical" && !isDone(t)).length;
    const atRisk = tasks.filter((t) => ["at_risk", "jeopardy"].includes(t.track_status) && !isDone(t)).length;
    const pct = progressPct(tasks, project?.progress_style);
    return { total, done, critical, atRisk, pct };
  }, [tasks, project?.progress_style]);

  const phases = useMemo(() => derivePhases(project, tasks), [project, tasks]);
  const phaseColors = useMemo(() => buildPhaseColors(phases, project?.phase_colors || {}), [phases, project?.phase_colors]);

  const setPhaseColor = async (g, color) => {
    try {
      await putProject({ phase_colors: { ...(project.phase_colors || {}), [g]: color } });
      toast.success(`"${g}" color updated`);
    } catch (e) { toast.error("Could not save the phase color"); }
  };

  const movePhase = async (g, dir) => {
    const idx = phases.indexOf(g);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= phases.length) return;
    const next = [...phases];
    [next[idx], next[j]] = [next[j], next[idx]];
    try { await putProject({ phases: next }); } catch (e) { toast.error("Could not move the phase"); }
  };

  const setPhaseNumber = async (g, newIdx) => {
    const idx = phases.indexOf(g);
    if (idx < 0 || newIdx === idx || newIdx < 0 || newIdx >= phases.length) return;
    const next = [...phases];
    next.splice(idx, 1);
    next.splice(newIdx, 0, g);
    try {
      await putProject({ phases: next });
      toast.success(`"${g}" is now Phase ${newIdx + 1}`);
    } catch (e) { toast.error("Could not renumber the phase"); }
  };

  const setPhaseLabel = async (g, label) => {
    const next = { ...(project?.phase_labels || {}) };
    if (label) next[g] = label;
    else delete next[g];
    try {
      await putProject({ phase_labels: next });
      toast.success(label ? `"${g}" is now Phase ${label}` : `"${g}" is back to its automatic number`);
    } catch (e) { toast.error("Could not save the phase label"); }
  };

  const applyMoves = async (moves) => {
    try {
      const { data } = await api.post(`/projects/${id}/tasks/reorder`, { moves });
      setTasks(data);
    } catch (e) {
      toast.error("Could not move the task");
    }
  };
  const byOrder = (a, b) => (a.order || 0) - (b.order || 0);
  const childPhaseMoves = (taskId, phase) =>
    tasks.filter((t) => t.parent_id === taskId).map((c) => ({ id: c.id, order: c.order || 0, phase }));

  const moveTask = async (taskId, targetPhase, beforeId) => {
    const moved = tasks.find((t) => t.id === taskId);
    if (!moved || taskId === beforeId) return;
    const target = tasks.filter((t) => t.phase === targetPhase && isTopLevel(t) && t.id !== taskId).sort(byOrder);
    let idx = beforeId ? target.findIndex((t) => t.id === beforeId) : target.length;
    if (idx < 0) idx = target.length;
    target.splice(idx, 0, moved);
    const moves = target.map((t, i) => ({
      id: t.id, order: (i + 1) * 10, phase: targetPhase,
      ...(t.id === taskId ? { set_parent: true, parent_id: null } : {}),
    }));
    await applyMoves([...moves, ...childPhaseMoves(taskId, targetPhase)]);
  };

  const isDescendantOf = (candidateId, ancestorId) => {
    let p = tasks.find((t) => t.id === candidateId);
    const seen = new Set();
    while (p?.parent_id && !seen.has(p.id)) {
      seen.add(p.id);
      if (p.parent_id === ancestorId) return true;
      p = tasks.find((t) => t.id === p.parent_id);
    }
    return false;
  };

  const nestTask = async (taskId, parentId) => {
    const parent = tasks.find((t) => t.id === parentId);
    if (!parent || taskId === parentId) return;
    if (isDescendantOf(parentId, taskId)) {
      toast.error("Can't nest a task inside its own subtask");
      return;
    }
    const sibs = tasks.filter((t) => t.parent_id === parentId && t.id !== taskId).sort(byOrder);
    const moves = [{ id: taskId, order: (sibs.length + 1) * 10, phase: parent.phase, set_parent: true, parent_id: parentId }];
    await applyMoves([...moves, ...childPhaseMoves(taskId, parent.phase)]);
    toast.success("Nested as a subtask");
  };

  const insertBeforeSibling = async (taskId, target) => {
    if (isDescendantOf(target.id, taskId)) {
      toast.error("Can't move a task inside its own subtask");
      return;
    }
    const moved = tasks.find((t) => t.id === taskId);
    if (!moved) return;
    const sibs = tasks.filter((t) => t.parent_id === target.parent_id && t.id !== taskId).sort(byOrder);
    let idx = sibs.findIndex((t) => t.id === target.id);
    if (idx < 0) idx = sibs.length;
    sibs.splice(idx, 0, moved);
    const moves = sibs.map((t, i) => ({
      id: t.id, order: (i + 1) * 10, phase: target.phase,
      ...(t.id === taskId ? { set_parent: true, parent_id: target.parent_id || null } : {}),
    }));
    await applyMoves([...moves, ...childPhaseMoves(taskId, target.phase)]);
  };

  const linkChain = async (ids) => {
    const seq = ids.map((i) => tasks.find((t) => t.id === i)).filter(Boolean);
    if (seq.length < 2) return;
    const prev = tasks;
    try {
      let data = null;
      for (let i = 1; i < seq.length; i++) {
        const t = seq[i], pred = seq[i - 1];
        const deps = (t.dependencies || []).map((d) => (typeof d === "string" ? { predecessor_id: d } : d));
        if (deps.some((d) => d.predecessor_id === pred.id)) continue;
        ({ data } = await api.patch(`/tasks/${t.id}`, { dependencies: [...deps, { predecessor_id: pred.id, type: "FS", lag: 0 }] }));
      }
      if (data) setTasks(data);
      toast.success(`Linked ${seq.length} tasks in sequence (finish → start)`);
      if (data) noteReschedules(prev, data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not link the tasks");
      load();
    }
  };

  const unlinkChain = async (ids) => {
    const idset = new Set(ids);
    try {
      let data = null, removed = 0;
      for (const tid of ids) {
        const t = tasks.find((x) => x.id === tid);
        if (!t) continue;
        const deps = (t.dependencies || []).map((d) => (typeof d === "string" ? { predecessor_id: d } : d));
        const kept = deps.filter((d) => !idset.has(d.predecessor_id));
        if (kept.length !== deps.length) {
          removed += deps.length - kept.length;
          ({ data } = await api.patch(`/tasks/${t.id}`, { dependencies: kept }));
        }
      }
      if (data) setTasks(data);
      toast.success(removed ? `Removed ${removed} link${removed > 1 ? "s" : ""} between the selected tasks` : "No links found between the selected tasks");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not unlink the tasks");
      load();
    }
  };

  const buildIoMap = async (specs, generateTasks) => {
    try {
      const { data } = await api.post(`/projects/${id}/io-map/build`, {
        phases: specs.map((s) => ({ ...s, delivery_date: s.delivery_date || null })),
        generate_tasks: !!generateTasks,
      });
      setTasks(data);
      setPlanView("flow");
      toast.success(generateTasks ? "IO Map built — Maestro drafted starter tasks under each goal" : "IO Map built — every phase now has a goal and a promised output");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not build the IO Map");
    }
  };

  const flowLinkInput = async (taskId, predId) => {
    const t = tasks.find((x) => x.id === taskId);
    if (!t) return;
    const deps = (t.dependencies || []).map((d) => (typeof d === "string" ? { predecessor_id: d } : d));
    if (deps.some((d) => d.predecessor_id === predId)) return;
    const prev = tasks;
    const data = await inlineUpdate(t, { dependencies: [...deps, { predecessor_id: predId, type: "FS", lag: 0 }] });
    toast.success("Input linked — this phase now consumes upstream work");
    noteReschedules(prev, data);
  };

  const flowCreateMilestone = async (phase, title) => {
    const items = tasks.filter((t) => t.phase === phase && !t.is_milestone && t.end_date);
    const last = [...items].sort((a, b) => (a.end_date > b.end_date ? -1 : 1))[0];
    const d = last?.end_date || project?.end_date || new Date().toISOString().slice(0, 10);
    await saveTask({
      title, phase, is_milestone: true, status: "todo", priority: "medium",
      start_date: d, end_date: d,
      dependencies: last ? [{ predecessor_id: last.id, type: "FS", lag: 0 }] : [],
    });
  };

  const moveTasksBulk = async (ids, targetPhase, beforeId) => {
    const movedSet = new Set(ids);
    const moved = ids.map((i) => tasks.find((t) => t.id === i)).filter(Boolean);
    if (!moved.length) return;
    const target = tasks.filter((t) => t.phase === targetPhase && isTopLevel(t) && !movedSet.has(t.id)).sort(byOrder);
    let idx = beforeId && !movedSet.has(beforeId) ? target.findIndex((t) => t.id === beforeId) : target.length;
    if (idx < 0) idx = target.length;
    target.splice(idx, 0, ...moved);
    const moves = target.map((t, i) => ({
      id: t.id, order: (i + 1) * 10, phase: targetPhase,
      ...(movedSet.has(t.id) ? { set_parent: true, parent_id: null } : {}),
    }));
    const childMoves = ids.flatMap((i) => childPhaseMoves(i, targetPhase));
    await applyMoves([...moves, ...childMoves]);
    toast.success(`${moved.length} item${moved.length === 1 ? "" : "s"} moved`);
  };

  const dropTask = (srcId, target, phase, mode) => {
    if (Array.isArray(srcId)) {
      const ids = srcId.filter((x) => !target || x !== target.id);
      if (!ids.length) return;
      return moveTasksBulk(ids, phase, target && isTopLevel(target) ? target.id : null);
    }
    if (!srcId || (target && srcId === target.id)) return;
    if (mode === "nest") return nestTask(srcId, target.id);
    if (!target) return moveTask(srcId, phase, null);
    if (isTopLevel(target)) return moveTask(srcId, phase, target.id);
    return insertBeforeSibling(srcId, target);
  };
  const isTopLevel = (t) => !t.parent_id || !tasks.some((p) => p.id === t.parent_id);

  const openNew = () => { setEditTask(null); setTaskSeed(null); setTaskOpen(true); };
  const openNewSeeded = (seed) => { setEditTask(null); setTaskSeed(seed); setTaskOpen(true); };
  const openLink = (taskId) => { setLinkTaskId(taskId || null); setLinkOpen(true); };
  const noteReschedules = (prev, next, excludeIds = []) => {
    if (!prev || !next) return;
    const ex = new Set(excludeIds);
    const before = Object.fromEntries(prev.map((t) => [t.id, `${t.start_date || ""}|${t.end_date || ""}`]));
    const changed = next.filter((t) => !ex.has(t.id) && before[t.id] !== undefined && before[t.id] !== `${t.start_date || ""}|${t.end_date || ""}`);
    if (!changed.length) return;
    toast.info(`${changed.length} task date${changed.length > 1 ? "s were" : " was"} rescheduled to respect the links`, {
      description: changed.slice(0, 3).map((t) => t.title).join(", ") + (changed.length > 3 ? ` +${changed.length - 3} more` : ""),
    });
  };

  const saveLinks = async (task, deps) => {
    const prev = tasks;
    const data = await inlineUpdate(task, { dependencies: deps });
    toast.success(`Links updated for "${task.title}"`);
    noteReschedules(prev, data);
  };
  const openTask = (t) => { setEditTask(t); setTaskOpen(true); };

  const applyTemplate = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/apply-template`, {});
      setTasks(data);
      toast.success(`${project?.methodology || ""} template plan added`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No template available for this plan type");
    }
  };

  const saveTask = async (payload, taskId) => {
    try {
      const prev = tasks;
      if (taskId) {
        const { data } = await api.put(`/tasks/${taskId}`, payload);
        setTasks(data);
        const old = prev.find((t) => t.id === taskId);
        const newPct = payload.percent_complete;
        if (old?.status === "done" && newPct != null && newPct < 100) {
          toast.info(`"${old.title}" reopened at ${newPct}% — it's no longer marked done`);
        } else {
          toast.success("Task updated");
        }
        noteReschedules(prev, data, [taskId]);
      } else {
        const { data } = await api.post(`/projects/${id}/tasks`, payload);
        setTasks(data);
        toast.success("Task added");
      }
      if (weekendAdjusted(payload)) toast.info("A date fell on a weekend — moved to the next Monday (non-working day)");
      setTaskOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const weekendAdjusted = (payload) => {
    if (project?.work_on_weekends) return false;
    const w = (s) => { if (!s) return false; const d = new Date(s + "T12:00:00"); return d.getDay() === 0 || d.getDay() === 6; };
    return w(payload.start_date) || w(payload.end_date);
  };

  const inlineUpdate = async (task, patch) => {
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, patch);
      setTasks(data);
      if (patch.percent_complete != null && patch.percent_complete < 100 && task.status === "done" && !patch.status) {
        toast.info(`"${task.title}" reopened at ${patch.percent_complete}% — it's no longer marked done`);
      }
      if ((patch.start_date || patch.end_date) && weekendAdjusted(patch)) toast.info("A date fell on a weekend — moved to the next Monday (non-working day)");
      return data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
      load();
    }
  };

  const trueUpTask = async (t, percent) => {
    try {
      await api.post(`/tasks/${t.id}/confirm-progress`, { percent });
      if ((t.status === "done" || (t.percent_complete || 0) >= 100) && percent < 100) {
        toast.info(`"${t.title}" reopened at ${percent}% — it's no longer marked done`);
      } else {
        toast.success(`"${t.title}" trued-up at ${percent}%`);
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "True-up failed"); }
  };
  const duplicateTask = async (t) => {
    try {
      const { data } = await api.post(`/tasks/${t.id}/duplicate`);
      setTasks(data);
      toast.success(`Duplicated "${t.title}" — click the copy to edit it`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Duplicate failed"); }
  };
  const renamePhase = async (oldName, newName) => {
    try {
      await api.patch(`/projects/${id}/phases`, { old_name: oldName, new_name: newName, phases });
      toast.success(`Phase renamed to "${newName}"`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rename failed"); }
  };
  const addPhase = async (name) => {
    try {
      await api.post(`/projects/${id}/phases`, { name, phases });
      toast.success(`Phase "${name}" added`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add phase"); }
  };
  const duplicatePhase = async (g, newName) => {
    try {
      const { data } = await api.post(`/projects/${id}/phases/duplicate`, { phase: g, new_name: newName || null });
      toast.success(`Phase cloned as "${data.new_phase}" (${data.cloned} item${data.cloned === 1 ? "" : "s"})`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not duplicate the phase"); throw e; }
  };
  const [phaseAction, setPhaseAction] = useState(null);
  const [phaseImportOpen, setPhaseImportOpen] = useState(false);
  const savePhaseTemplate = async (g, name) => {
    try {
      await api.post("/phase-templates", { project_id: id, phase: g, name });
      toast.success(`"${name}" saved to your Library`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save the template"); throw e; }
  };
  const deletePhase = async (g) => {
    const count = tasks.filter((t) => t.phase === g).length;
    if (!window.confirm(`Delete the phase "${g}"${count ? ` and its ${count} item${count === 1 ? "" : "s"}` : ""}? This can't be undone.`)) return;
    try {
      const { data } = await api.post(`/projects/${id}/phases/delete`, { phase: g });
      toast.success(`Phase "${g}" deleted${data.deleted ? ` (${data.deleted} item${data.deleted === 1 ? "" : "s"})` : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete the phase"); }
  };

  const deleteTask = async (taskId) => {    try {
      await api.delete(`/tasks/${taskId}`);
      setTasks((ts) => ts.filter((t) => t.id !== taskId));
      setTaskOpen(false);
      toast.success("Task deleted");
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  const changeStatus = async (taskId, status) => {
    setTasks((ts) => ts.map((t) => (t.id === taskId ? { ...t, status } : t)));
    try {
      await api.patch(`/tasks/${taskId}/status`, { status });
    } catch (e) {
      toast.error("Status update failed");
      load();
    }
  };

  const runSummary = async () => {
    setSummaryOpen(true);
    setSummaryLoading(true);
    setSummary("");
    try {
      const { data } = await api.post(`/projects/${id}/ai/summary`);
      setSummary(data.summary);
    } catch (err) {
      setSummary("");
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
      setSummaryOpen(false);
    } finally {
      setSummaryLoading(false);
    }
  };

  const removeProject = async () => {
    if (!window.confirm("Delete this project and all its tasks?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    nav("/");
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></Layout>;

  const kpiZone = (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-zone">
      <Kpi label="Total tasks" value={stats.total} icon={ListTodo} accent="#0044FF" onClick={() => setStatOpen("all")} />
      <Kpi label="Critical" value={stats.critical} icon={AlertTriangle} accent="#EF4444" onClick={() => setStatOpen("critical")} />
      <Kpi label="At risk" value={stats.atRisk} icon={ShieldAlert} accent="#F59E0B" onClick={() => setStatOpen("at_risk")} />
      <Kpi label="Complete" value={`${stats.done} · ${stats.pct}%`} icon={CheckCircle2} accent="#10B981" onClick={() => setStatOpen("complete")} />
    </div>
  );

  return (
    <Layout>
      <button onClick={() => nav("/dashboard")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-projects">
        <ChevronLeft className="h-4 w-4" /> Projects
      </button>

      {/* Header zone */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-8">
        <div className="min-w-0">
          {allProjects.length > 1 && (
            <div className="mb-2 max-w-xs" data-testid="project-switcher">
              <Select value={id} onValueChange={(v) => nav(`/projects/${v}?tab=${tab}`)}>
                <SelectTrigger className="h-9" data-testid="project-switcher-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allProjects.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`project-switch-option-${p.id}`}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {editingName ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                className="font-display text-4xl font-extrabold tracking-tight border-b-2 border-primary bg-transparent outline-none min-w-0 flex-1"
                data-testid="project-name-input" />
              <Button size="sm" onClick={saveName} disabled={savingName || !nameDraft.trim()} data-testid="project-name-save">
                {savingName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingName(false)} data-testid="project-name-cancel">Cancel</Button>
            </div>
          ) : (
            <div className="group/title flex items-center gap-2.5">
              <h1 onClick={() => setTab("plan")} title="Go to the plan (WBS)" className="font-display text-4xl font-extrabold tracking-tight cursor-pointer hover:text-primary transition-colors" data-testid="project-title">{project.name}</h1>
              <button type="button" title="Rename this project" onClick={() => { setNameDraft(project.name); setEditingName(true); }}
                className="opacity-0 group-hover/title:opacity-100 transition-opacity text-muted-foreground hover:text-primary shrink-0" data-testid="project-name-edit">
                <Pencil className="h-4 w-4" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
            {project.industry && <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {project.industry}</span>}
          </div>
          {editingDesc ? (
            <div className="mt-3 max-w-2xl space-y-2">
              <textarea rows={3} autoFocus value={descDraft} onChange={(e) => setDescDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") setEditingDesc(false); if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveDescription(); }}
                placeholder="What is this project about? Goals, scope, context…"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-y"
                data-testid="project-desc-input" />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={saveDescription} disabled={savingDesc} data-testid="project-desc-save">
                  {savingDesc ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingDesc(false)} data-testid="project-desc-cancel">Cancel</Button>
                <span className="text-[10px] text-muted-foreground">Ctrl+Enter to save · Esc to cancel</span>
              </div>
            </div>
          ) : project.description ? (
            <p className="group text-muted-foreground mt-3 max-w-2xl cursor-text hover:text-foreground transition-colors" title="Click to edit the description"
               onClick={() => { setDescDraft(project.description || ""); setEditingDesc(true); }} data-testid="project-desc-text">
              {project.description} <Pencil className="inline h-3 w-3 align-baseline opacity-0 group-hover:opacity-60 transition-opacity" />
            </p>
          ) : (
            <button type="button" className="mt-3 text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1.5 transition-colors"
               onClick={() => { setDescDraft(""); setEditingDesc(true); }} data-testid="project-desc-add">
              <Pencil className="h-3 w-3" /> Add a project description
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {(() => { const isMember = project?.my_role === "member"; return (<>
          <Button variant="outline" className={`gap-2 relative ${trueup && !trueup.pending_count ? "text-muted-foreground" : ""}`} onClick={() => setTrueupOpen(true)} data-testid="open-trueup-button">
            <CalendarClock className="h-4 w-4" /> True-up
            {trueup?.pending_count > 0
              ? <span className="ml-0.5 text-[11px] font-bold px-1.5 rounded-full bg-primary text-primary-foreground" data-testid="trueup-count-badge">{trueup.pending_count}</span>
              : trueup && <span className="ml-0.5 inline-flex items-center gap-0.5 text-[11px] font-bold px-1.5 rounded-full bg-gray-200 text-gray-500" title="All true-ups complete" data-testid="trueup-done-badge"><CheckCircle2 className="h-3 w-3" /></span>}
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setMembersOpen(true)} data-testid="open-members-button"><UserPlus className="h-4 w-4" /> Team</Button>
          {!isMember && <Button variant="outline" size="icon" onClick={() => setSettingsOpen(true)} data-testid="open-settings-button" aria-label="Project settings"><Settings className="h-4 w-4" /></Button>}
          {!isMember && <Button variant="outline" className="gap-2" onClick={() => setSaveTemplateOpen(true)} disabled={!tasks.length} data-testid="save-as-template-button"><BookMarked className="h-4 w-4" /> Save as template</Button>}
          <Button variant="outline" className="gap-2" onClick={() => setExecOpen(true)} disabled={!tasks.length} data-testid="open-exec-summary-button"><Presentation className="h-4 w-4" /> Exec summary</Button>
          <Button variant="outline" className="gap-2" onClick={runSummary} disabled={!tasks.length} data-testid="ai-summary-button"><FileText className="h-4 w-4" /> AI summary</Button>
          {!isMember && <Button variant="outline" className="gap-2" onClick={() => setAiOpen(true)} data-testid="open-ai-plan-button"><Sparkles className="h-4 w-4" /> AI plan</Button>}
          {!isMember && <Button className="gap-2" onClick={openNew} data-testid="add-task-button"><Plus className="h-4 w-4" /> Task</Button>}
          </>); })()}
        </div>
      </div>

      <ProjectMembers open={membersOpen} onOpenChange={setMembersOpen} projectId={id} projectName={project?.name} />

      {/* Archived warning banner */}
      {project?.archived && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="archived-banner">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800 flex-1 min-w-[220px]"><b>This project is archived.</b> It's hidden from your dashboard, so changes you make here are easy to lose track of. Restore it to keep working.</p>
          <Button size="sm" className="gap-1.5 shrink-0" onClick={async () => {
            try { await api.post(`/projects/${id}/unarchive`); toast.success("Project restored to your dashboard"); load(); }
            catch { toast.error("Could not restore"); }
          }} data-testid="archived-restore-button"><Archive className="h-3.5 w-3.5" /> Restore project</Button>
        </div>
      )}

      {/* Deadline countdown — wizard plans built backward from a target date */}
      {project?.wizard_mode === "backward" && project?.wizard_start && !project?.archived && (() => {
        const today = new Date(new Date().toDateString());
        const start = new Date(project.wizard_start + "T00:00:00");
        const target = project.wizard_target_end ? new Date(project.wizard_target_end + "T00:00:00") : null;
        if (target && target < today) return null;
        if (tasks.some((t) => (t.percent_complete || 0) > 0 || t.status === "in_progress" || t.status === "done")) return null;
        const days = Math.round((start - today) / 86400000);
        const fd = (d) => d?.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
        const late = days < 0;
        return (
          <div className={`mb-4 rounded-xl border px-4 py-3 flex items-center gap-3 flex-wrap ${late ? "border-red-300 bg-red-50" : "border-blue-300 bg-blue-50"}`} data-testid="deadline-countdown-banner">
            <Hourglass className={`h-4 w-4 shrink-0 ${late ? "text-red-600" : "text-blue-600"}`} />
            <p className={`text-sm flex-1 min-w-[220px] ${late ? "text-red-800" : "text-blue-900"}`}>
              {late
                ? <><b>Work needed to start {Math.abs(days)} day{Math.abs(days) === 1 ? "" : "s"} ago</b> ({fd(start)}) to hit your {fd(target)} target at full pace — log progress, or rebuild the plan with compression.</>
                : days === 0
                  ? <><b>Work must start today</b> to hit your target of {fd(target)}.</>
                  : <><b><span data-testid="deadline-countdown-days">{days}</span> day{days === 1 ? "" : "s"} until work must start</b> ({fd(start)}) to hit your target of {fd(target)}.</>}
            </p>
          </div>
        );
      })()}

      {/* Completed plan → learned wizard template suggestion */}
      {!wizardSuggestHidden && !project?.archived && tasks.length > 2
        && tasks.filter((t) => !t.is_summary).every((t) => t.status === "done" || (t.percent_complete || 0) >= 100)
        && localStorage.getItem(`wizard-tpl-suggest-${id}`) !== "1" && (
        <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="learned-template-banner">
          <GraduationCap className="h-4 w-4 text-emerald-600 shrink-0" />
          <p className="text-sm text-emerald-900 flex-1 min-w-[220px]"><b>This plan is complete.</b> Turn it into a learned wizard template — future plans of this type get realistic durations from what actually happened.</p>
          <Button size="sm" className="gap-1.5 shrink-0 bg-emerald-600 hover:bg-emerald-700" onClick={() => setPlanTemplateOpen(true)} data-testid="learned-template-banner-cta"><Wand2 className="h-3.5 w-3.5" /> Turn into wizard template</Button>
          <button type="button" onClick={() => { localStorage.setItem(`wizard-tpl-suggest-${id}`, "1"); setWizardSuggestHidden(true); }} className="text-emerald-700/70 hover:text-emerald-900 shrink-0" title="Dismiss" data-testid="learned-template-banner-dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* True-up due banner */}
      {trueup?.is_due && !bannerDismissed && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 mb-6" data-testid="trueup-banner">
          <CalendarClock className="h-5 w-5 text-primary shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Progress true-up is due</p>
            <p className="text-xs text-muted-foreground">Confirm each task's % complete to keep your roadmap accurate. {trueup.pending_count} task{trueup.pending_count === 1 ? "" : "s"} awaiting confirmation.</p>
          </div>
          <Button size="sm" className="gap-1.5 shrink-0" onClick={() => setTrueupOpen(true)} data-testid="banner-start-trueup"><CalendarClock className="h-4 w-4" /> Start true-up</Button>
          <button onClick={() => setBannerDismissed(true)} className="text-muted-foreground hover:text-foreground shrink-0" data-testid="banner-dismiss" aria-label="Dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* KPI zone */}
      <div className="mb-8">{kpiZone}</div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="w-fit">
          <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/60 mb-0.5 pl-1" data-testid="project-menu-label">Project Level Menu</p>
          <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-border bg-secondary/50 p-1.5 w-fit" data-testid="project-tabs">
          {TAB_GROUPS.map((g) => {
            const activeItem = g.items.find(([v]) => v === tab);
            return (
              <DropdownMenu key={g.label}>
                <DropdownMenuTrigger asChild>
                  <button className={`h-9 px-3.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${activeItem ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                          data-testid={`tabcat-${g.label.toLowerCase()}`}>
                    {g.label}
                    {g.label === "Communicate" && chatUnread > 0 && tab !== "pit" && (
                      <span className="text-[10px] font-bold px-1.5 py-px rounded-full bg-red-500 text-white" title={`${chatUnread} unread Orchestra Pit message${chatUnread === 1 ? "" : "s"}`} data-testid="communicate-unread-badge">{chatUnread > 99 ? "99+" : chatUnread}</span>
                    )}
                    {g.label === "Track" && (radarInfo?.count || 0) > 0 && tab !== "radar" && (
                      <span className={`text-[10px] font-bold px-1.5 py-px rounded-full ${radarInfo.due_count ? "bg-red-500 text-white" : "bg-sky-100 text-sky-700"}`} title={`${radarInfo.count} item${radarInfo.count === 1 ? "" : "s"} on the radar${radarInfo.due_count ? ` — ${radarInfo.due_count} due to revisit` : ""}`} data-testid="track-radar-badge">{radarInfo.count}</span>
                    )}
                    {activeItem && <span className="text-primary">· {activeItem[1]}</span>}
                    <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {g.items.map(([v, l]) => (
                    <DropdownMenuItem key={v} onClick={() => (v === "statusreport" ? nav(`/status-report?project=${id}`) : v === "ioflow" ? (setPlanView("flow"), setTab("plan")) : setTab(v))}
                                      className={(tab === v || (v === "ioflow" && tab === "plan" && planView === "flow")) ? "font-semibold text-primary bg-primary/5" : ""}
                                      data-testid={`tab-${v}`}>
                      {l}
                      {v === "pit" && chatUnread > 0 && (
                        <span className="ml-auto text-[10px] font-bold px-1.5 py-px rounded-full bg-red-500 text-white" data-testid="pit-unread-badge">{chatUnread > 99 ? "99+" : chatUnread}</span>
                      )}
                      {v === "radar" && (radarInfo?.count || 0) > 0 && (
                        <span className={`ml-auto text-[10px] font-bold px-1.5 py-px rounded-full ${radarInfo.due_count ? "bg-red-500 text-white" : "bg-sky-100 text-sky-700"}`} data-testid="radar-menu-badge">{radarInfo.count}</span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })}
          </div>
        </div>

        <TabsContent value="mywork" className="mt-6">
          <MyWorkView projectId={id} />
        </TabsContent>

        <TabsContent value="plan" className="mt-6">
          {tasks.length === 0 ? (
            <div className="space-y-4">
              <EmptyTasks onAi={() => setAiOpen(true)} onManual={openNew} onTemplate={applyTemplate} methodology={project?.methodology} onIoMap={isIoMap ? () => setIoMapOpen(true) : null} />
              <div className="text-center">
                <Button variant="outline" className="gap-1.5" onClick={() => setImportOpen(true)} data-testid="wbs-import-empty"><FileUp className="h-4 w-4" /> Import from MS Project / Excel / CSV</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6" data-testid="plan-view">
              <div className="flex items-center justify-between gap-3">
                <PhaseProgress tasks={tasks} phases={phases} phaseColors={phaseColors} />
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-1 rounded-lg border border-border p-1 w-fit" data-testid="plan-view-toggle">
                  <button type="button" onClick={() => setPlanView("wbs")} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${planView === "wbs" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="plan-view-wbs">Spreadsheet (WBS)</button>
                  <button type="button" onClick={() => setPlanView("list")} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${planView === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="plan-view-list">List</button>
                  <button type="button" onClick={() => setPlanView("flow")} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${planView === "flow" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="plan-view-flow">IO Flow</button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className={`gap-1.5 ${trueup && !trueup.pending_count ? "text-muted-foreground" : ""}`} onClick={() => setTrueupOpen(true)} data-testid="plan-trueup">
                    <CalendarClock className="h-3.5 w-3.5" /> True-up
                    {trueup?.pending_count > 0
                      ? <span className="text-[10px] font-bold px-1.5 rounded-full bg-primary text-primary-foreground" data-testid="plan-trueup-count">{trueup.pending_count}</span>
                      : trueup && <span className="inline-flex items-center text-[10px] font-bold px-1.5 rounded-full bg-gray-200 text-gray-500" title="All true-ups complete" data-testid="plan-trueup-done"><CheckCircle2 className="h-3 w-3" /></span>}
                  </Button>
                  <Button size="sm" className="gap-1.5" onClick={() => openNewSeeded(null)} data-testid="plan-add-task"><Plus className="h-3.5 w-3.5" /> Add task</Button>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => openNewSeeded({ is_milestone: true })} data-testid="plan-add-milestone"><span className="text-xs leading-none">▼</span> Add milestone</Button>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => openLink(null)} data-testid="plan-link-tasks"><Link2 className="h-3.5 w-3.5" /> Link tasks</Button>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setImportOpen(true)} data-testid="wbs-import"><FileUp className="h-3.5 w-3.5" /> Import</Button>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => exportWbsCsv(project.name, phases, tasks)} data-testid="wbs-export-csv"><Download className="h-3.5 w-3.5" /> Export CSV</Button>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => printWbs(project.name, phases, tasks, phaseColors)} data-testid="wbs-print"><Printer className="h-3.5 w-3.5" /> Print</Button>
                  <Button variant="outline" size="sm" className="gap-1.5" title="Save this plan's tasks, durations and dependencies as a reusable wizard template"
                    onClick={() => setPlanTemplateOpen(true)} data-testid="wbs-save-template"><BookMarked className="h-3.5 w-3.5" /> Save as template</Button>
                  <PlanToProcessButton projectId={id} source={planView === "flow" ? "flow" : "wbs"} />
                </div>
              </div>
              {planView === "wbs" ? (
                <WbsTable phases={phases} tasks={tasks} phaseColors={phaseColors} progressStyle={project?.progress_style} collapsed={planCollapsed} onToggle={togglePlanPhase} onOpenTask={openTask} onInlineUpdate={inlineUpdate} onAddTask={(phase, isMilestone, isSummary) => openNewSeeded({ ...(phase ? { phase } : {}), is_milestone: !!isMilestone, is_summary: !!isSummary })} onLinkTask={(t) => openLink(t.id)} onDropTask={dropTask} onTrueUp={trueUpTask} onDuplicate={duplicateTask} onRenamePhase={renamePhase} onAddPhase={addPhase} onPhaseColor={setPhaseColor} onMovePhase={movePhase} onDuplicatePhase={(g) => setPhaseAction({ type: "clone", phase: g })} onSaveTemplate={(g) => setPhaseAction({ type: "template", phase: g })} onImportPhase={() => setPhaseImportOpen(true)} onDeletePhase={deletePhase} onBulkMove={(ids, phase) => moveTasksBulk(ids, phase, null)} onBulkLink={linkChain} onBulkUnlink={unlinkChain} summaryColor={project?.summary_color} onSummaryColor={(c) => putProject({ summary_color: c }).then(() => toast.success("Summary row color updated")).catch(() => toast.error("Could not save the color"))} onDelete={(t) => { if (window.confirm(`Delete "${t.title}"? This can't be undone.`)) deleteTask(t.id); }} />
              ) : planView === "flow" ? (
                <PhaseFlowView projectId={id} projectName={project?.name} progressStyle={project?.progress_style} phases={phases} tasks={tasks} autoNudgePhases={project?.auto_nudge_phases} phaseColors={phaseColors} onPhaseColor={setPhaseColor} onReorderPhase={setPhaseNumber} phaseLabels={project?.phase_labels} onPhaseLabel={setPhaseLabel} onSetFlowRole={async (t, role) => { const { data } = await api.patch(`/tasks/${t.id}`, { flow_role: role }); setTasks(data); }} onOpenTask={openTask} onLinkInput={flowLinkInput} onCreateMilestone={flowCreateMilestone} fetchTips={() => api.post(`/projects/${id}/ai/flow-suggestions`).then((r) => r.data)} onSetup={isIoMap ? () => setIoMapOpen(true) : null} flowColors={project?.flow_colors} onFlowColors={(fc) => putProject({ flow_colors: fc }).then(() => toast.success("Flow colors updated")).catch(() => toast.error("Could not save the colors"))} onTipToAction={async (tip, phase) => {
                  const { data: item } = await api.post(`/projects/${id}/action-items`, {
                    title: tip.length > 140 ? `${tip.slice(0, 137)}…` : tip,
                    description: `From Maestro's IO flow review of the "${phase}" phase.\n\n${tip}`,
                    priority: "high", status: "open", source: "general",
                  });
                  toast.success(`Action item ${item.display_id || ""} created from Maestro's tip`);
                }} />
              ) : (
              <div className="space-y-6">
              {phases.map((g) => {
                const items = tasks.filter((t) => t.phase === g).sort((a, b) => (a.order || 0) - (b.order || 0));
                if (!items.length) return null;
                const isOpen = !planCollapsed[g];
                return (
                  <div key={g} className="border border-border rounded-lg overflow-hidden">
                    <button type="button" onClick={() => togglePlanPhase(g)} className="w-full flex items-center gap-2 px-4 py-3 border-b border-border text-left hover:brightness-95 transition-all" style={{ background: `${phaseColors[g]}0d` }} data-testid={`plan-phase-toggle-${g}`}>
                      {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: phaseColors[g] }} />
                      <span className="font-display font-bold">{g}</span>
                      <span className="text-xs text-muted-foreground">{items.length}</span>
                    </button>
                    {isOpen && (
                    <div className="divide-y divide-border"
                      onDragOver={(e) => { if (e.dataTransfer.types.includes("text/plain")) e.preventDefault(); }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const src = e.dataTransfer.getData("text/plain"); if (src) moveTask(src, g, null); }}>
                      {items.map((t) => {
                        const sm = statusMeta(t.status);
                        const pm = priorityMeta(t.priority);
                        return (
                          <button key={t.id} onClick={() => openTask(t)}
                            draggable={isTopLevel(t)}
                            onDragStart={(e) => { e.dataTransfer.setData("text/plain", t.id); e.dataTransfer.effectAllowed = "move"; }}
                            onDragOver={(e) => { if (isTopLevel(t) && e.dataTransfer.types.includes("text/plain")) { e.preventDefault(); e.stopPropagation(); } }}
                            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const src = e.dataTransfer.getData("text/plain"); if (src && src !== t.id) moveTask(src, g, t.id); }}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/40 transition-colors text-left" data-testid={`plan-task-${t.id}`}>
                            {isTopLevel(t) && <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0 cursor-grab" data-testid={`plan-list-grip-${t.id}`} />}
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: sm.color }} />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">{t.title}</p>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                {t.knowledge_area && <span>{t.knowledge_area}</span>}
                                {t.assignee && <span>{t.assignee}</span>}
                                {t.start_date && <span>{t.start_date} → {t.end_date}</span>}
                              </div>
                            </div>
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded shrink-0" style={{ color: pm.color, background: `${pm.color}1a` }}>{pm.label}</span>
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded shrink-0" style={{ color: sm.color, background: `${sm.color}1a` }}>{sm.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    )}
                  </div>
                );
              })}
              </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <TimelineView projectId={id} tasks={tasks} onOpenTask={openTask} phases={phases} phaseColors={phaseColors} />
        </TabsContent>

        <TabsContent value="map" className="mt-6">
          {tasks.length === 0 ? <EmptyTasks onAi={() => setAiOpen(true)} onManual={openNew} onTemplate={applyTemplate} methodology={project?.methodology} /> : <><PhaseProgress tasks={tasks} phases={phases} phaseColors={phaseColors} progressStyle={project?.progress_style} /><ProjectMap projectId={id} phases={phases} onOpenTask={openTask} progressStyle={project?.progress_style} /></>}
        </TabsContent>

        <TabsContent value="roadmap" className="mt-6">
          {tasks.length === 0 ? <EmptyTasks onAi={() => setAiOpen(true)} onManual={openNew} onTemplate={applyTemplate} methodology={project?.methodology} /> : <><PhaseProgress tasks={tasks} phases={phases} phaseColors={phaseColors} progressStyle={project?.progress_style} /><RoadmapView projectId={id} onOpenTask={openTask} durationUnit={project?.duration_unit} phaseColors={phaseColors} progressStyle={project?.progress_style} /></>}
        </TabsContent>

        <TabsContent value="board" className="mt-6">
          <div className="inline-flex rounded-md border border-border overflow-hidden mb-5">
            <button onClick={() => setBoardView("board")} className={`px-3 py-1.5 text-sm font-medium ${boardView === "board" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`} data-testid="board-view-board">Board</button>
            <button onClick={() => setBoardView("backlog")} className={`px-3 py-1.5 text-sm font-medium ${boardView === "backlog" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`} data-testid="board-view-backlog">Story Backlog</button>
          </div>
          {boardView === "backlog" ? (
            <StoryBacklog projectId={id} tasks={tasks} />
          ) : tasks.length === 0 ? <EmptyTasks onAi={() => setAiOpen(true)} onManual={openNew} onTemplate={applyTemplate} methodology={project?.methodology} /> : <KanbanBoard projectId={id} tasks={tasks} onStatusChange={changeStatus} onOpenTask={openTask} phaseColors={phaseColors} />}
        </TabsContent>

        <TabsContent value="teams" className="mt-6">
          {tasks.length === 0 ? <EmptyTasks onAi={() => setAiOpen(true)} onManual={openNew} onTemplate={applyTemplate} methodology={project?.methodology} /> : <RaciView tasks={tasks} phases={phases} phaseColors={phaseColors} onOpenTask={openTask} />}
        </TabsContent>

        <TabsContent value="reports" className="mt-6">
          {tasks.length === 0 ? <EmptyTasks onAi={() => setAiOpen(true)} onManual={openNew} onTemplate={applyTemplate} methodology={project?.methodology} /> : <ReportsView tasks={tasks} phaseColors={phaseColors} onOpenTask={openTask} projectId={id} progressStyle={project?.progress_style} />}
        </TabsContent>

        <TabsContent value="raid" className="mt-6">
          <RaidView projectId={id} tasks={tasks} />
        </TabsContent>

        <TabsContent value="radar" className="mt-6">
          {tab === "radar" && <RaidView key="radar-entry" projectId={id} tasks={tasks} initialSub="radar" />}
        </TabsContent>

        <TabsContent value="risks" className="mt-6">
          <RaidView projectId={id} tasks={tasks} initialSub="risks" />
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <AuditView projectId={id} />
        </TabsContent>

        <TabsContent value="artifacts" className="mt-6">
          <ArtifactsView projectId={id} />
        </TabsContent>

        <TabsContent value="approvals" className="mt-6">
          <ApprovalsView projectId={id} />
        </TabsContent>

        <TabsContent value="cards" className="mt-6">
          <CardsView projectId={id} />
        </TabsContent>

        <TabsContent value="actions" className="mt-6">
          <RaidView projectId={id} tasks={tasks} initialSub="actions" />
        </TabsContent>

        <TabsContent value="budget" className="mt-6">
          <BudgetView projectId={id} phaseColors={phaseColors} />
        </TabsContent>

        <TabsContent value="resources" className="mt-6">
          <ResourcesView projectId={id} tasks={tasks} onOpenTask={openTask} onChanged={load}
            emptyTasks={<EmptyTasks onAi={() => setAiOpen(true)} onManual={openNew} onTemplate={applyTemplate} methodology={project?.methodology} />} />
        </TabsContent>

        <TabsContent value="stakeholders" className="mt-6">
          <StakeholderView projectId={id} />
        </TabsContent>

        <TabsContent value="baseline" className="mt-6">
          {tasks.length === 0 ? <EmptyTasks onAi={() => setAiOpen(true)} onManual={openNew} onTemplate={applyTemplate} methodology={project?.methodology} /> : <BaselineView projectId={id} project={project} tasks={tasks} phaseColors={phaseColors} onReload={load} />}
        </TabsContent>

        <TabsContent value="meeting" className="mt-6">
          {tasks.length === 0 ? <EmptyTasks onAi={() => setAiOpen(true)} onManual={openNew} onTemplate={applyTemplate} methodology={project?.methodology} /> : <MeetingPrep projectId={id} tasks={tasks} phaseColors={phaseColors} />}
        </TabsContent>

        <TabsContent value="notebook" className="mt-6">
          <NotebookView projectId={id} />
        </TabsContent>

        <TabsContent value="pit" className="mt-6">
          <OrchestraPit projectId={id} onRead={() => setChatUnread(0)} onOpenTask={openTask} />
        </TabsContent>

        <TabsContent value="process" className="mt-6">
          <ProcessView projectId={id} />
        </TabsContent>
      </Tabs>

      {project?.my_role !== "member" && (
      <div className="mt-12 pt-6 border-t border-border flex flex-wrap gap-2">
        <Button variant="ghost" className="text-muted-foreground hover:bg-secondary gap-2" onClick={() => setArchiveConfirmOpen(true)} data-testid="project-archive-button"><Archive className="h-4 w-4" /> Archive project</Button>
        <Button variant="ghost" className="text-destructive hover:bg-destructive/10 gap-2" onClick={removeProject} data-testid="delete-project-button">
          <Trash2 className="h-4 w-4" /> Delete project
        </Button>
      </div>
      )}

      <AlertDialog open={archiveConfirmOpen} onOpenChange={setArchiveConfirmOpen}>
        <AlertDialogContent data-testid="archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Archive className="h-5 w-5 text-amber-600" /> Archive "{project?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This project will be <b>hidden from your dashboard, cards and week-at-a-glance</b>. Nothing is deleted — you can restore it anytime from Library → Archived Projects. Heads up: edits made while a project is archived are easy to lose track of.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-confirm-cancel">Keep it active</AlertDialogCancel>
            <AlertDialogAction className="bg-amber-600 hover:bg-amber-700" onClick={async () => {
              try {
                await api.post(`/projects/${id}/archive`);
                toast.success(`"${project.name}" archived — find it under Library → Archived Projects`, {
                  action: { label: "Undo", onClick: async () => {
                    try { await api.post(`/projects/${id}/unarchive`); toast.success("Project restored"); window.location.reload(); }
                    catch { toast.error("Could not restore"); }
                  } },
                });
                nav("/dashboard");
              } catch { toast.error("Could not archive project"); }
            }} data-testid="archive-confirm-button">Archive project</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TaskDialog open={taskOpen} onOpenChange={setTaskOpen} task={editTask} allTasks={tasks} phases={phases} seed={taskSeed} onSave={saveTask} onDelete={deleteTask} projectId={id} onTruedUp={load} />
      <LinkTasksDialog open={linkOpen} onOpenChange={setLinkOpen} tasks={tasks} phases={phases} initialTaskId={linkTaskId} onSave={saveLinks} />
      <IoMapBuilderDialog open={ioMapOpen} onOpenChange={setIoMapOpen} defaultPhases={phases} onBuild={buildIoMap} />
      <StatTasksDialog open={!!statOpen} onOpenChange={(v) => !v && setStatOpen(null)} filterKey={statOpen || "all"} tasks={tasks} phaseColors={phaseColors} onOpenTask={(t) => { setStatOpen(null); openTask(t); }} />
      <AIPlanDialog open={aiOpen} onOpenChange={setAiOpen} project={project} onGenerated={(data) => setTasks(data)} />
      <TrueUpDialog open={trueupOpen} onOpenChange={setTrueupOpen} projectId={id} onDone={() => { load(); }} />
      <ImportWizard open={importOpen} onOpenChange={setImportOpen} projectId={id} onImported={() => load()} />
      <ProjectSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} project={project} onSaved={(p) => { setProject(p); loadTrueup(); }} />
      <PhaseNameDialog action={phaseAction} onOpenChange={(v) => !v && setPhaseAction(null)} onConfirm={(name) => (phaseAction.type === "clone" ? duplicatePhase(phaseAction.phase, name) : savePhaseTemplate(phaseAction.phase, name))} />
      <PhaseImportDialog open={phaseImportOpen} onOpenChange={setPhaseImportOpen} projectId={id} onImported={load} />
      <ExecSummaryDialog open={execOpen} onOpenChange={setExecOpen} projectId={id} />

      <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="summary-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Progress summary</DialogTitle></DialogHeader>
          {summaryLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-10 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Analyzing project...</div>
          ) : (
            <Markdown text={summary} />
          )}
        </DialogContent>
      </Dialog>

      <SaveTemplateDialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen} projectId={id} defaultName={project.name ? `${project.name} template` : ""} />
      <SavePlanTemplateDialog open={planTemplateOpen} onOpenChange={setPlanTemplateOpen} projectId={id}
        defaultName={project.name ? `${project.name} template` : ""}
        defaultLearned={tasks.length > 2 && tasks.filter((t) => !t.is_summary).every((t) => t.status === "done" || (t.percent_complete || 0) >= 100)} />
    </Layout>
  );
}

function Kpi({ label, value, icon: Icon, accent, onClick }) {
  const iconColor = accent || "#0044FF";
  return (
    <button type="button" onClick={onClick} className="border border-border rounded-lg p-5 text-left w-full hover:border-foreground/30 hover:shadow-sm transition-all cursor-pointer" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="overline text-muted-foreground">{label}</p>
        <span
          className="h-9 w-9 rounded-lg flex items-center justify-center shadow-sm"
          style={{ background: iconColor, boxShadow: `0 4px 12px -2px ${iconColor}66` }}
        >
          <Icon className="h-[18px] w-[18px] text-white" strokeWidth={2.5} />
        </span>
      </div>
      <p className="font-display text-3xl font-extrabold tracking-tight mt-2" style={accent ? { color: accent } : {}}>{value}</p>
    </button>
  );
}

function EmptyTasks({ onAi, onManual, onTemplate, methodology, onIoMap }) {
  return (
    <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="tasks-empty">
      <Sparkles className="h-8 w-8 text-primary mx-auto mb-3" />
      <h3 className="font-display text-2xl font-bold tracking-tight">Start your plan</h3>
      <p className="text-muted-foreground mt-2 mb-6 max-w-md mx-auto">{onIoMap ? "Define each phase's Input, Summary (goal) and promised Output milestone — the IO Map builder walks you through it." : `Use a ready-made ${methodology || ""} template, generate a plan with AI, or add tasks manually.`}</p>
      <div className="flex flex-wrap justify-center gap-2">
        {onIoMap && <Button className="gap-2" onClick={onIoMap} data-testid="empty-io-map-button"><ListTodo className="h-4 w-4" /> Build your IO Map</Button>}
        {onTemplate && !onIoMap && <Button className="gap-2" onClick={onTemplate} data-testid="empty-template-button"><ListTodo className="h-4 w-4" /> Use {methodology || "plan"} template</Button>}
        <Button variant={onTemplate || onIoMap ? "outline" : "default"} className="gap-2" onClick={onAi} data-testid="empty-ai-plan-button"><Sparkles className="h-4 w-4" /> Build with AI</Button>
        <Button variant="outline" className="gap-2" onClick={onManual} data-testid="empty-add-task-button"><Plus className="h-4 w-4" /> Add task</Button>
      </div>
    </div>
  );
}
